import { useState } from "react";

import type { CampaignConflictItem } from "@/@types/campaign-conflict";
import { useToast } from "@/components/ui/use-toast";
import { useUpdatePro014GradeRestrictionMutation } from "@/services/campaign-pro014";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";

import { RESTRICTION_DISABLED, RESTRICTION_ENABLED } from "../components/grade/grade-messages";
import type { GradeRestrictionToggleState } from "../types";
import { MESSAGES } from "../utils/messages";
import {
  conflictsFromError,
  describeApiError,
  itemCodesFromError,
} from "../utils/pro014-api-errors";

export function useGradeRestrictionToggle(campaignCode: number): GradeRestrictionToggleState {
  const { toast } = useToast();
  const [updateRestriction, { isLoading: isSaving }] = useUpdatePro014GradeRestrictionMutation();
  const [conflicts, setConflicts] = useState<CampaignConflictItem[]>([]);
  const [itemsWithoutGrade, setItemsWithoutGrade] = useState<string[]>([]);

  const handleError = (error: unknown): void => {
    const found: CampaignConflictItem[] = conflictsFromError(error);
    if (found.length > 0) {
      setConflicts(found);
      return;
    }
    const missingGrade: string[] = itemCodesFromError(error);
    if (missingGrade.length > 0) {
      setItemsWithoutGrade(missingGrade);
      toast({
        ...MESSAGES.GRADE_RESTRICTION.ERROR,
        description: extractRtkErrorMessage(error, MESSAGES.GRADE_RESTRICTION.ERROR.description),
      });
      return;
    }
    toast({
      ...MESSAGES.GRADE_RESTRICTION.ERROR,
      description: describeApiError(error, MESSAGES.GRADE_RESTRICTION.ERROR.description),
    });
  };

  const toggle = async (enabled: boolean): Promise<void> => {
    try {
      await updateRestriction({ code: campaignCode, enabled }).unwrap();
    } catch (error: unknown) {
      handleError(error);
      return;
    }
    setItemsWithoutGrade([]);
    toast({
      ...MESSAGES.GRADE_RESTRICTION.SUCCESS,
      description: enabled ? RESTRICTION_ENABLED : RESTRICTION_DISABLED,
    });
  };

  const handleGradeSaved = (itemCode: string, hasGrade: boolean): void => {
    if (!hasGrade) return;
    setItemsWithoutGrade((previous: string[]) =>
      previous.filter((pending: string) => pending !== itemCode),
    );
  };

  return {
    isSaving,
    conflicts,
    itemsWithoutGrade,
    toggle,
    handleGradeSaved,
    closeConflicts: () => setConflicts([]),
  };
}
